import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import axios from "axios";
import { usePostContext } from "../context/PostContext";
import { useUserContext } from "../context/UserContext";
import TagFriends from "./TagFriends";
import { API_BASE, MEDIA_BASE } from "../config";
import UserAvatar from "../helpers/UserAvatar";

const AddPost = ({ FetchPosts, groupId, autoTagUserId }) => {
    const [content, setContent] = useState("");
    const [taggedUsers, setTaggedUsers] = useState([]);
    const [files, setFiles] = useState([]);
    const [linkPreview, setLinkPreview] = useState('');
    const [mentUserIds, setMentUserIds] = useState([]);
    const [mentGroupIds, setMentGroupIds] = useState([]);
    const [mentCompanyIds, setMentCompanyIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileProgresses, setFileProgresses] = useState([]);
    const [error, setError] = useState(null);
    const [mentionResults, setMentionResults] = useState({ users: [], groups: [], companies: [] });
    const [mentionLoading, setMentionLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingLinkPreview, setLoadingLinkPreview] = useState(false);
    const [privacy, setPrivacy] = useState(0);

    const inputRef = useRef(null);
    const hasAutoTagged = useRef(false);
    const searchDebounce = useRef(null);
    const { getPosts } = usePostContext();
    const { myFreinds, activeProfile } = useUserContext();
    const { auth_data } = useUserContext();
    const auth = auth_data;

    const chekStrg = (strg) => {
        if (strg == "https://buzzinguniverse.com/backend/") {
            return null;
        } else {
            return strg
        }
    }

    function generateUniqueString() {
        const timestamp = Date.now();
        const randomPart = Math.random().toString(36).substring(2, 10);
        return `${timestamp}-${randomPart}`;
    }


    useEffect(() => {
        const spans = [...inputRef.current.querySelectorAll(".mention")];
        setMentUserIds(spans.filter(el => !el.getAttribute("mention_type") || el.getAttribute("mention_type") === "user").map(el => parseInt(el.getAttribute("nav_id"))));
        setMentGroupIds(spans.filter(el => el.getAttribute("mention_type") === "group").map(el => parseInt(el.getAttribute("nav_id"))));
        setMentCompanyIds(spans.filter(el => el.getAttribute("mention_type") === "company").map(el => parseInt(el.getAttribute("nav_id"))));
    }, [content]);

    // Auto-tag user when autoTagUserId prop is provided
    useEffect(() => {
        // Only proceed if autoTagUserId is a valid number or numeric string
        if (!autoTagUserId || autoTagUserId === false || !myFreinds || myFreinds.length === 0) {
            return;
        }

        // Prevent duplicate auto-tagging
        if (hasAutoTagged.current) {
            return;
        }

        // Find the user in myFreinds by ID
        const userToTag = myFreinds.find(friend => friend.user_data.id === parseInt(autoTagUserId));

        if (userToTag) {
            // Check if user is not already tagged
            const isAlreadyTagged = taggedUsers.some(u => u.user_data.id === userToTag.user_data.id);

            if (!isAlreadyTagged) {
                setTaggedUsers(prev => [...prev, userToTag]);
                hasAutoTagged.current = true; // Mark as auto-tagged
            }
        }
    }, [autoTagUserId, myFreinds]);


    const extractURL = (text) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const match = text.match(urlRegex);
        return match ? match[0] : null;
    };

    const fetchLinkPreview = async (url) => {
        setLoadingLinkPreview(true);
        try {
            const response = await axios.get(`https://buzzinguniverse.com/backend/api/get-meta-data?url=${encodeURIComponent(url)}`);
            setLinkPreview({ ...response.data, url: url });
        } catch (err) {
            console.error("Error fetching link preview:", err);
            setLinkPreview('');
        } finally {
            setLoadingLinkPreview(false);
        }
    };

    const handleInputChange = (e) => {
        const newHtml = e.target.innerHTML;
        const newText = e.target.textContent;
        setContent(newHtml);

        const atIndex = newText.lastIndexOf('@');
        if (atIndex !== -1) {
            const searchQuery = newText.substring(atIndex + 1);
            // Close if query too long or has line breaks — user has moved on
            if (searchQuery.length > 40 || searchQuery.includes('\n')) {
                setShowDropdown(false);
                if (searchDebounce.current) clearTimeout(searchDebounce.current);
            } else {
                setShowDropdown(true);
                if (searchDebounce.current) clearTimeout(searchDebounce.current);
                if (searchQuery.length === 0) {
                    setMentionResults({ users: [], groups: [], companies: [] });
                    setMentionLoading(false);
                } else {
                    setMentionLoading(true);
                    searchDebounce.current = setTimeout(async () => {
                        try {
                            const res = await axios.get(`${API_BASE}/api/search`, { params: { keyword: searchQuery } });
                            const d = res.data.data || res.data;
                            const apiUsers = d.users || [];
                            // Include self if matches and not already in results
                            const selfUser = { id: auth?.id, first_name: auth?.fname, last_name: auth?.lname, photo: auth?.photo };
                            const q = searchQuery.toLowerCase();
                            const selfFull = `${selfUser.first_name} ${selfUser.last_name}`.toLowerCase();
                            const selfMatches = selfFull.includes(q) || selfUser.first_name?.toLowerCase().includes(q) || selfUser.last_name?.toLowerCase().includes(q);
                            const usersWithSelf = selfMatches && !apiUsers.find(u => u.id === auth?.id)
                                ? [selfUser, ...apiUsers]
                                : apiUsers;
                            setMentionResults({
                                users: usersWithSelf,
                                groups: d.groups || [],
                                companies: d.companies || [],
                            });
                        } catch {
                            setMentionResults({ users: [], groups: [], companies: [] });
                        } finally {
                            setMentionLoading(false);
                        }
                    }, 300);
                }
            }
        } else {
            setShowDropdown(false);
            if (searchDebounce.current) clearTimeout(searchDebounce.current);
        }

        const detectedURL = extractURL(newText);
        if (detectedURL && !content.includes(`<a href="${detectedURL}"`)) {
            fetchLinkPreview(detectedURL);
            const updatedContent = newText.replace(
                detectedURL,
                `<a href="${detectedURL}" target="_blank">${detectedURL}</a>&nbsp;`
            );

            setContent(updatedContent);
            inputRef.current.innerHTML = updatedContent;

            setTimeout(() => {
                const el = inputRef.current;
                el.focus();
                const range = document.createRange();
                const sel = window.getSelection();
                range.selectNodeContents(el);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }, 0);
        }
    };

    const handleMentionClick = (item, type) => {
        const displayName = type === "user"
            ? `${item.first_name} ${item.last_name}`
            : item.name;
        const textBeforeMention = content.substring(0, content.lastIndexOf("@"));
        const mentionHTML = `<span class="mention" nav_id="${item.id}" mention_type="${type}" nav_name="${encodeURIComponent(item.name || '')}">${displayName}</span>&nbsp;`;
        const updatedContent = `${textBeforeMention}${mentionHTML}`;
        inputRef.current.innerHTML = updatedContent;
        setContent(updatedContent);
        setShowDropdown(false);

        setTimeout(() => {
            const el = inputRef.current;
            el.focus();
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }, 0);
    };

    const handleFileChange = (e) => {
        const uploadedFiles = Array.from(e.target.files);
        const validFiles = uploadedFiles.filter(file => file.size <= 512 * 1024 * 1024);

        if (validFiles.length < uploadedFiles.length) {
            setError("Some files exceed the maximum limit of 512MB and were not added.");
        } else {
            setError(null);
        }

        if (validFiles.length > 0) {
            setFiles(validFiles);
            setUploadProgress(0);
            setFileProgresses(validFiles.map(() => ({ progress: 0, status: 'Pending' })));
        }
    };

    const removeFile = (indexToRemove) => {
        const updatedFiles = files.filter((_, index) => index !== indexToRemove);
        const updatedProgresses = fileProgresses.filter((_, index) => index !== indexToRemove);
        setFiles(updatedFiles);
        setFileProgresses(updatedProgresses);

        if (updatedFiles.length === 0) {
            setUploadProgress(0);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!auth?.id || (!content && files.length === 0)) {
            setError("Please enter details or upload a file.");
            return;
        }

        setLoading(true);
        setError(null);
        const taggedUserIds = taggedUsers.map(user => user.user_data.id);
        const live_prv = document.querySelector('#linkPRV');

        try {
            // Generate unique key only for multiple files
            const uniqueKey = files.length > 1 ? generateUniqueString() : null;

            // If multiple files, loop through and upload each with the same unique_key
            if (files.length > 0) {
                const totalFiles = files.length;

                for (let i = 0; i < files.length; i++) {
                    // Update status for current file
                    setFileProgresses(prev =>
                        prev.map((item, idx) =>
                            idx === i ? { ...item, status: 'Uploading' } : item
                        )
                    );

                    const formData = new FormData();
                    formData.append("user_id", auth?.id);
                    formData.append("type", 1);
                    formData.append("privacy", privacy);
                    formData.append("tag_ids", mentUserIds);
                    formData.append("mention_group_ids", mentGroupIds);
                    formData.append("mention_company_ids", mentCompanyIds);
                    formData.append("tagged_ids", taggedUserIds);
                    const effectiveGroupId = groupId || (activeProfile?.id ?? null);
                    effectiveGroupId && formData.append("group_id", effectiveGroupId);
                    formData.append("title", "posted an update");
                    formData.append("details", content + (live_prv ? live_prv.innerHTML : ''));
                    formData.append("file", files[i]);

                    // Only add unique_key if multiple files
                    if (uniqueKey) {
                        formData.append("unique_key", uniqueKey);
                    }

                    const response = await axios.post(
                        "https://buzzinguniverse.com/backend/api/post-request",
                        formData,
                        {
                            headers: { "Content-Type": "multipart/form-data" },
                            onUploadProgress: (progressEvent) => {
                                const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

                                // Update individual file progress
                                setFileProgresses(prev =>
                                    prev.map((item, idx) =>
                                        idx === i ? { ...item, progress: fileProgress } : item
                                    )
                                );

                                // Calculate overall progress
                                const completedFiles = i;
                                const overallProgress = Math.round(((completedFiles * 100 + fileProgress) / totalFiles));
                                setUploadProgress(overallProgress);
                            },
                        }
                    );

                    if (response.status !== 200) {
                        throw new Error(response.data.message || "Something went wrong.");
                    }

                    // Mark file as completed
                    setFileProgresses(prev =>
                        prev.map((item, idx) =>
                            idx === i ? { ...item, progress: 100, status: 'Completed' } : item
                        )
                    );
                }
            } else {
                // No files, just post content
                const formData = new FormData();
                formData.append("user_id", auth?.id);
                formData.append("type", 1);
                formData.append("privacy", privacy);
                formData.append("tag_ids", mentUserIds);
                formData.append("mention_group_ids", mentGroupIds);
                formData.append("mention_company_ids", mentCompanyIds);
                formData.append("tagged_ids", taggedUserIds);
                const effectiveGroupIdNoFile = groupId || (activeProfile?.id ?? null);
                effectiveGroupIdNoFile && formData.append("group_id", effectiveGroupIdNoFile);
                formData.append("title", "posted an update");
                formData.append("details", content + (live_prv ? live_prv.innerHTML : ''));

                const response = await axios.post(
                    "https://buzzinguniverse.com/backend/api/post-request",
                    formData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    }
                );

                if (response.status !== 200) {
                    throw new Error(response.data.message || "Something went wrong.");
                }
            }

            // Refresh posts after successful upload
            if (FetchPosts) {
                await FetchPosts(1, 1);
            } else {
                await getPosts(auth?.id);
            }

            // Reset form after successful upload and refresh
            setContent("");
            setLinkPreview('');
            setFiles([]);
            setFileProgresses([]);
            setUploadProgress(0);
            inputRef.current.innerHTML = "";
            setMentUserIds([]);
            setMentGroupIds([]);
            setMentCompanyIds([]);
            setShowForm(false);
            setTaggedUsers([]);
            hasAutoTagged.current = false; // Reset auto-tag flag for next post
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    const postingPhoto = activeProfile
        ? activeProfile.photo
        : (chekStrg(auth?.photo) || '/images/default.png');

    const postingName = activeProfile ? activeProfile.name : auth?.fname;

    return (
        <div className="what-new-bejan-box">
            {activeProfile && (
                <div className="posting-as-bar">
                    <img src={activeProfile.photo} alt={activeProfile.name} className="posting-as-avatar" />
                    <span>Posting as <strong>{activeProfile.name}</strong> ({activeProfile.type === 'company' ? 'Company' : 'Group'})</span>
                </div>
            )}
            <form>
                <ul>
                    <li>
                        <Link to={activeProfile ? `/company/${activeProfile.id}/${encodeURIComponent(activeProfile.name)}` : `/member/${auth?.id}/profile`}>
                            <UserAvatar src={postingPhoto} alt="profile" />
                        </Link>
                    </li>
                    <li>
                        <div
                            ref={inputRef}
                            className="open_div"
                            contentEditable
                            spellCheck={false}
                            onInput={handleInputChange}
                            onClick={() => setShowForm(true)}
                        />
                        {!showForm && <div className="placeholder_div">What's New, {postingName}?</div>}
                        {showDropdown && (
                            <div className="friends-dropdown mention-dropdown">
                                {mentionLoading && <div className="mention-dropdown-loading">Searching...</div>}
                                {!mentionLoading && mentionResults.users.length === 0 && mentionResults.groups.length === 0 && mentionResults.companies.length === 0 && (
                                    <div className="mention-dropdown-empty">No results found</div>
                                )}
                                {mentionResults.users.length > 0 && (
                                    <>
                                        <div className="mention-dropdown-header">Users</div>
                                        {mentionResults.users.map((user) => (
                                            <div key={`user-${user.id}`} className="mention-dropdown-item" onClick={() => handleMentionClick(user, "user")}>
                                                <img src={(user.photo && `${MEDIA_BASE}/${user.photo}`) || "/images/default.png"} alt="" />
                                                <div className="friends-dropdown-info">
                                                    <span className="friends-dropdown-name">{user.first_name} {user.last_name}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                                {mentionResults.groups.length > 0 && (
                                    <>
                                        <div className="mention-dropdown-header">Groups</div>
                                        {mentionResults.groups.map((group) => (
                                            <div key={`group-${group.id}`} className="mention-dropdown-item" onClick={() => handleMentionClick(group, "group")}>
                                                <img src={(group.profile_picture && `${MEDIA_BASE}/${group.profile_picture}`) || "/images/default.png"} alt="" />
                                                <div className="friends-dropdown-info">
                                                    <span className="friends-dropdown-name">{group.name}</span>
                                                    <span className="friends-dropdown-username mention-type-badge">Group</span>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                                {mentionResults.companies.length > 0 && (
                                    <>
                                        <div className="mention-dropdown-header">Companies</div>
                                        {mentionResults.companies.map((company) => (
                                            <div key={`company-${company.id}`} className="mention-dropdown-item" onClick={() => handleMentionClick(company, "company")}>
                                                <img src={(company.profile_picture && `${MEDIA_BASE}/${company.profile_picture}`) || "/images/default.png"} alt="" />
                                                <div className="friends-dropdown-info">
                                                    <span className="friends-dropdown-name">{company.name}</span>
                                                    <span className="friends-dropdown-username mention-type-badge">Company</span>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                        {linkPreview && !loadingLinkPreview && linkPreview.title && (
                            <>
                                <span className="btn-removeLink" onClick={() => setLinkPreview('')}>x</span>
                                <div id="linkPRV">
                                    <a href={linkPreview.url} target="_blank">
                                        <div className="link-preview">
                                            {linkPreview.image && (
                                                <img src={linkPreview.image} alt={linkPreview.title} className="link-preview-image" onError={(e) => e.target.style.display='none'} />
                                            )}
                                            <div className="link-preview-content">
                                                {linkPreview.site_name && <span className="link-preview-domain">{linkPreview.site_name}</span>}
                                                <h4 className="link-preview-title">{linkPreview.title}</h4>
                                                {linkPreview.description && <p className="link-preview-description">{linkPreview.description}</p>}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            </>
                        )}
                        {loadingLinkPreview && (
                            <div className="loadin m-4">
                                {/* <span>Loading preview...</span> */}
                            </div>
                        )}
                    </li>
                </ul>
                {showForm && (
                    <div className="what-new-bejan-box-add-media">
                        <ul className="max-file-size-box">
                            <li>
                                <input type="file" multiple className="form-control" onChange={handleFileChange} accept="image/*,video/*,audio/*,.pdf" />
                                {files.length > 0 && !loading && (
                                    <div className="selected-files-list">
                                        {files.map((file, idx) => {
                                            const { name, type } = file;
                                            const fileExt = name.split('.').pop().toUpperCase();
                                            const isImage = type.startsWith("image/");
                                            const isVideo = type.startsWith("video/");

                                            const isPdf = type === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
                                            let preview;
                                            if (isImage) {
                                                preview = <img src={URL.createObjectURL(file)} alt={name} className="file-thumbnail-small" />;
                                            } else if (isVideo) {
                                                preview = <div className="file-icon-small">VIDEO</div>;
                                            } else if (isPdf) {
                                                preview = <div className="file-icon-small pdf-icon-small">PDF</div>;
                                            } else {
                                                preview = <div className="file-icon-small">{fileExt}</div>;
                                            }

                                            return (
                                                <div key={idx} className="selected-file-item">
                                                    <div className="file-preview-small">{preview}</div>
                                                    <div className="file-name-small">{name}</div>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn"
                                                        onClick={() => removeFile(idx)}
                                                        title="Remove file"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {loading && files.length > 0 && (
                                    <div className="overall-progress">
                                        <div className="overall-progress-label">
                                            <span>Overall Progress</span>
                                            <span>{uploadProgress}%</span>
                                        </div>
                                        <div className="progress-bar-container">
                                            <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                                        </div>
                                    </div>
                                )}
                            </li>
                            <li><span>Max. File Size: 512MB</span></li>
                            {!groupId && (
                                <li>
                                    <select className="form-select" value={privacy} onChange={(e) => setPrivacy(e.target.value)}>
                                        <option value={1}>Private</option>
                                        <option value={0}>Public</option>
                                    </select>
                                </li>
                            )}
                            <TagFriends myFreinds={myFreinds} setTaggedUsers={setTaggedUsers} taggedUsers={taggedUsers} />
                        </ul>
                        {taggedUsers.length > 0 && (
                            <div className="tf-tagged-users">
                                <strong>{auth.fname}</strong> is with&nbsp;
                                {taggedUsers.slice(0, 3).map((u, index) => (
                                    <span key={u.user_data.id} className="tf-tag-badge ms-1">
                                        {u.user_data.first_name} {u.user_data.last_name}
                                        {index < Math.min(2, taggedUsers.length - 1) && ','}
                                    </span>
                                ))}
                                {taggedUsers.length > 3 && (
                                    <span className="tf-tag-badge ms-1">
                                        and {taggedUsers.length - 3} others
                                    </span>
                                )}
                            </div>
                        )}

                        {loading && files.length > 0 && fileProgresses.length > 0 && (
                            <div className="files-progress-list">
                                {files.map((file, idx) => {
                                    const { name, type, size } = file;
                                    const fileSizeKB = (size / 1024).toFixed(2);
                                    const uploadedSizeKB = ((fileProgresses[idx]?.progress || 0) / 100 * size / 1024).toFixed(2);
                                    const fileExt = name.split('.').pop().toUpperCase();
                                    const isImage = type.startsWith("image/");
                                    const isVideo = type.startsWith("video/");
                                    const isAudio = type.startsWith("audio/");
                                    const isPdfFile = type === 'application/pdf' || name.toLowerCase().endsWith('.pdf');

                                    let preview;
                                    if (isImage) {
                                        preview = <img src={URL.createObjectURL(file)} alt={name} className="file-thumbnail" />;
                                    } else if (isVideo) {
                                        preview = <div className="file-icon">VIDEO</div>;
                                    } else if (isAudio) {
                                        preview = <div className="file-icon">AUDIO</div>;
                                    } else if (isPdfFile) {
                                        preview = <div className="file-icon pdf-icon">PDF</div>;
                                    } else {
                                        preview = <div className="file-icon">{fileExt}</div>;
                                    }

                                    return (
                                        <div key={idx} className="file-bar">
                                            <div className="file-preview">{preview}</div>
                                            <div className="file-details">
                                                <div className="file-name">{name}</div>
                                                <div className="file-meta">
                                                    <span>{uploadedSizeKB} KB / {fileSizeKB} KB</span>
                                                    {fileProgresses[idx]?.status === 'Completed' && <span className="status completed">• Completed</span>}
                                                    {fileProgresses[idx]?.status === 'Uploading' && <span className="status uploading">• Uploading</span>}
                                                    {fileProgresses[idx]?.status === 'Pending' && <span className="status pending">• Pending</span>}
                                                </div>
                                                <div className="progress-bar-container">
                                                    <div className="progress-bar" style={{ width: `${fileProgresses[idx]?.progress || 0}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <ul className="post-in-profile">
                            <li>
                                <select name="whats-new-post-in" className="form-select">
                                    <option value="profile">Post in: Profile</option>
                                    <option value="group">Post in: Group</option>
                                </select>
                            </li>
                            <li>
                                <a onClick={() => setShowForm(false)} className="activity-canel">Cancel</a>
                                <a onClick={handleSubmit} className={`activity-post ${loading && "loadin"}`} disabled={loading}>
                                    {loading ? "Posting" : "Post Update"}
                                </a>
                            </li>
                        </ul>
                        {error && <p className="error-message">{error}</p>}
                    </div>
                )}
            </form>
        </div>
    );
};

export default AddPost;
